import { useTranslations } from "@jugl-web/utils";
import { environment } from "@web-src/environments/environment";
import React, { useCallback } from "react";
import { Button } from "@jugl-web/ui-components/cross-platform";
import LoginMethodButton from "./components/LoginMethodButton";
import { ReactComponent as EmailIcon } from "./assets/email.svg";
import { ReactComponent as GoogleIcon } from "./assets/google.svg";
import { ReactComponent as AppleIcon } from "./assets/apple.svg";
import { ReactComponent as LinkedinIcon } from "./assets/linkedin.svg";
import { ReactComponent as PhoneIcon } from "./assets/phone.svg";
import { LoginMethod } from "../../../../types";
import { useLoginPage } from "../../../../providers/LoginPageProvider/useLoginPage";

const LoginMethodPicker: React.FC = () => {
  const { setLoginMethod, loginMethod } = useLoginPage();
  const { t } = useTranslations();
  const loginWithSocial = useCallback(
    (provider: "linkedin" | "google" | "apple") => {
      const redirectUrl = `${window.location.origin}/jugl-callback`;
      window.open(
        `${environment.apiUrl}/jugl/uberauth/${provider}?final_callback_url=${redirectUrl}&agt=web`,
        "_self"
      );
    },
    []
  );

  return (
    <>
      {loginMethod === LoginMethod.email && (
        <div className="w-full pb-[26px] 2xl:pb-[88.5px]">
          <Button
            fullWidth
            variant="outlined"
            color="grey"
            className="text-grey-900 group relative h-[50px] border-none"
            onClick={() => loginWithSocial("google")}
          >
            <div className="absolute inset-0 rounded-md bg-[#E9E9E9] opacity-100 transition-opacity duration-300 group-hover:opacity-0" />
            <div className="absolute inset-0 rounded-md bg-gradient-to-r from-[#2124E4] to-[#B815C0] opacity-0 transition-opacity duration-300 group-hover:opacity-100" />

            <div className="absolute inset-[2px] flex items-center justify-center gap-[10px] rounded-md bg-white text-xs font-normal">
              <GoogleIcon />
              {t({
                id: "login-form.continue-with-google",
                defaultMessage: "Continue with Google",
              })}
            </div>
          </Button>
        </div>
      )}
      <div className="mt-auto flex flex-col gap-5 pb-4">
        <div className="flex items-center gap-5">
          <div className="bg-dark-100 h-px w-full" />
          <span className="text-grey-700 shrink-0 text-xs">
            {loginMethod === LoginMethod.phone
              ? t({
                  id: "login-page.login-with",
                  defaultMessage: "or login with",
                })
              : t({
                  id: "login-page.different-methods",
                  defaultMessage: "Different methods",
                })}
          </span>
          <div className="bg-dark-100 h-px w-full" />
        </div>
        <div className="flex items-center gap-5">
          {loginMethod === LoginMethod.email ? (
            <LoginMethodButton
              icon={<PhoneIcon />}
              title={t({
                id: "login-page.phone",
                defaultMessage: "Phone",
              })}
              onClick={() => setLoginMethod(LoginMethod.phone)}
            />
          ) : (
            <LoginMethodButton
              icon={<EmailIcon />}
              title={t({
                id: "login-page.email",
                defaultMessage: "Email",
              })}
              onClick={() => setLoginMethod(LoginMethod.email)}
            />
          )}
          {loginMethod === LoginMethod.phone && (
            <LoginMethodButton
              icon={<GoogleIcon />}
              title={t({
                id: "login-page.google",
                defaultMessage: "Google",
              })}
              onClick={() => loginWithSocial("google")}
            />
          )}
          <LoginMethodButton
            icon={<AppleIcon />}
            title={t({
              id: "login-page.apple",
              defaultMessage: "Apple",
            })}
            onClick={() => loginWithSocial("apple")}
          />
          <LoginMethodButton
            icon={<LinkedinIcon />}
            title={t({
              id: "login-page.linkedin",
              defaultMessage: "Linkedin",
            })}
            onClick={() => loginWithSocial("linkedin")}
          />
        </div>
      </div>
    </>
  );
};

export default LoginMethodPicker;
